import { useCallback, useEffect, useRef, useState } from 'react';
import { useActiveEditTarget } from '~/hooks/target/useActiveEditTarget';
import {
    ContourComparisonError,
    ContourComparisonMetrics,
    compareContours,
} from '~/domain/comparison/contourComparisonMetrics';
import { alignBinaryCrops } from '~/domain/comparison/contourMaskAlignment';
import { roiToBinaryCrop } from '~/engine/util/roiToMask';
import { handleError } from '~/utils/errors';
import type { RoiId } from '~/domain/changes/ids';

/**
 * Computes contour-comparison metrics (DSC / HD95 / MSD) for two ROIs of the
 * active structure set ENTIRELY in the browser: each ROI's GPU SDF texture is
 * read back to a binary mask, the two are aligned onto a shared voxel grid, and
 * the pure {@link compareContours} domain metric is run. No DICOM export, no
 * anonymisation, no backend upload, so no PHI leaves the client.
 *
 * The textures are read from the edit target's Environment, the one that
 * shows the active set in the grid. While the set sits on no view there is
 * no Environment to read, so the metrics are "not shown" rather than an
 * error: `submitPair` settles idle with no numbers, and
 * `structureSetShown` tells the panel to say why. `submitPair` changes
 * identity with the Environment, so a consumer that lists it as an effect
 * dependency recomputes once the set is shown again.
 *
 * The compute is deferred to a macrotask so the panel's loading state can paint
 * before the synchronous readback + surface-distance pass runs; a monotonic run
 * id discards results from a superseded pair (rapid ROI switches / edits).
 *
 * @returns submitPair: compute metrics for a reference + test ROI.
 * @returns cancelPending: drop any scheduled, not-yet-run compute (call when
 * the consumer stops caring about the result, e.g. compare mode closes).
 * @returns loading: true while a compute is scheduled or running.
 * @returns data: the latest metrics, or undefined before the first result.
 * @returns error: true when metrics are unavailable (e.g. an empty/point ROI).
 * @returns structureSetShown: false while the active set sits on no view, so
 * no metrics can be computed.
 */
export function useContourComparisonMetrics() {
    const { environment } = useActiveEditTarget();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [data, setData] = useState<ContourComparisonMetrics>();
    // Identifies the active compute; a stale macrotask sees a newer id and bails
    // so its result never overwrites the current pair's metrics.
    const runIdRef = useRef(0);
    const timeoutRef = useRef<number | undefined>(undefined);

    // A pending macrotask would otherwise still run its expensive readback +
    // metric compute (and set state) after the consumer unmounted.
    useEffect(() => {
        return () => {
            runIdRef.current += 1;
            window.clearTimeout(timeoutRef.current);
        };
    }, []);

    /** Drops the scheduled compute macrotask, if any. */
    const dropScheduledRun = useCallback(() => {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = undefined;
    }, []);

    const cancelPending = useCallback(() => {
        runIdRef.current += 1;
        dropScheduledRun();
        setLoading(false);
    }, [dropScheduledRun]);

    const submitPair = useCallback(
        (referenceRoiId: RoiId, testRoiId: RoiId) => {
            const runId = runIdRef.current + 1;
            runIdRef.current = runId;
            setLoading(true);
            setError(false);
            setData(undefined);

            // No view shows the set: nothing to read the masks from. Idle,
            // not an error; the panel names the state.
            if (!environment) {
                dropScheduledRun();
                setLoading(false);
                return;
            }

            // Supersede any still-scheduled run: only the latest pair matters.
            dropScheduledRun();
            timeoutRef.current = window.setTimeout(() => {
                // One guard suffices: the compute below is fully synchronous, so
                // once this passes the run can't be superseded mid-flight — a
                // newer submitPair or a cancel only bumps the run id between
                // macrotasks.
                if (runId !== runIdRef.current) {
                    return;
                }
                try {
                    const referenceRoi = environment.getRoi(referenceRoiId);
                    const testRoi = environment.getRoi(testRoiId);
                    if (!referenceRoi || !testRoi) {
                        throw new ContourComparisonError(
                            'One or both ROIs are no longer available.'
                        );
                    }
                    const aligned = alignBinaryCrops(
                        roiToBinaryCrop(referenceRoi),
                        roiToBinaryCrop(testRoi)
                    );
                    const metrics = compareContours(
                        aligned.reference,
                        aligned.test
                    );
                    setData(metrics);
                    setLoading(false);
                } catch (caught) {
                    setLoading(false);
                    setError(true);
                    // An empty/point/unavailable ROI is an expected "no metrics"
                    // state the panel surfaces inline; only escalate the
                    // genuinely unexpected failures.
                    if (!(caught instanceof ContourComparisonError)) {
                        handleError(
                            caught,
                            'An error occurred during contour comparison.'
                        );
                    }
                }
            }, 0);
        },
        [environment, dropScheduledRun]
    );

    return {
        submitPair,
        cancelPending,
        loading,
        data,
        error,
        structureSetShown: environment !== undefined,
    };
}
